/**
 * The order of operations behind the print button, free of React, Apollo and
 * the DOM so it can be tested. The hook
 * (`src/hooks/useContaminationDeclarationPrint.ts`) supplies the real tab, the
 * real query and the real toasts.
 *
 * The order is the whole point: the tab must exist *before* anything is
 * awaited. A click's transient user activation expires during the round trip,
 * and a `window.open` issued afterwards is swallowed by the popup blocker.
 */

/** The bits of a `Window` handle this flow uses — all cross-origin safe. */
export interface PrintTab {
    readonly closed: boolean;
    close(): void;
    navigate(url: string): void;
}

export interface PrintFileResult {
    declarationAccepted?: boolean | null;
    validity?: unknown;
    file?: { link?: string | null } | null;
}

export type PrintOutcome =
    /** The tab was pointed at the rendered PDF. */
    | 'opened'
    /** The browser refused the synchronous tab — pop-ups are disabled. */
    | 'popupBlocked'
    /** The backend withdrew `file`: the declaration stopped being printable. */
    | 'notPrintable'
    /** The lookup itself failed (network, 404, 500). */
    | 'failed';

export interface PrintDeclarationDeps {
    /** Must open synchronously; `null` means the popup blocker won. */
    openTab: () => PrintTab | null;
    /** Fetches a *fresh* presigned link — never a cached one. */
    fetchPrintFile: () => Promise<PrintFileResult | undefined>;
    /** Called with whatever the lookup learned, printable or not. */
    onState?: (result: PrintFileResult) => void;
    /** Reports the outcome to the user; not called for `opened`. */
    onError?: (outcome: Exclude<PrintOutcome, 'opened'>, err?: unknown) => void;
}

export const runPrintDeclaration = async ({
    openTab,
    fetchPrintFile,
    onState,
    onError,
}: PrintDeclarationDeps): Promise<PrintOutcome> => {
    const tab = openTab();
    if (!tab) {
        onError?.('popupBlocked');
        return 'popupBlocked';
    }

    try {
        const result = await fetchPrintFile();
        if (result) onState?.(result);

        const link = result?.file?.link;
        if (!link) {
            // `file` is null exactly when the declaration may not be printed —
            // an answer changed, or the consent was withdrawn, since it was read.
            if (!tab.closed) tab.close();
            onError?.('notPrintable');
            return 'notPrintable';
        }

        // The user may have closed the placeholder while we were waiting.
        if (!tab.closed) tab.navigate(link);
        return 'opened';
    } catch (err) {
        if (!tab.closed) tab.close();
        onError?.('failed', err);
        return 'failed';
    }
};
